// =======================================================================================================
import { Component } from 'react';
import _ from "lodash";
import { DashboardConfiguration, PageLayoutPolicy, PollingScale } from '@ModelViews/ModelViews';
import { DashboardHelper } from './DashboardStructs';
import { isEqualsSafe } from '@Utils/Utils';
import { ExDialog } from '@Primitive/ExDialog';
import { BoardThemeColorOverride, BoardThemeColorOverrideItems, BoardTheming } from "./BoardTheming";
import { IconButton } from '@Primitive/Button';
import { red200, grey100 } from '@Utils/Colors';
import { SettingsList } from '@Primitive/SettingsList';
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
// =======================================================================================================
export interface DashboardConfigurationViewProps {
    configurationKey: string;
    configuration: DashboardConfiguration;
    onConfigurationChange?: (key: string, newConf: DashboardConfiguration) => void;
    onConfigurationClose?: (key: string) => void;
    isOpen?: boolean;
    inEditMode: boolean;
    otherPages: DashboardConfiguration[];
}

export interface DashboardConfigurationViewState {
    configuration: DashboardConfiguration;
    errorMessagePageId: string;
}
// ------------------------------------------------------------------------------------------------------
export class DashboardConfigurationView extends Component<DashboardConfigurationViewProps, DashboardConfigurationViewState> {
    // ------------------------------------------------------------------------------------------------------
    private static MaxImageWidth: number = 1920 * 1.5;
    private static MaxImageHeight: number = 1080 * 1.5;
    // ------------------------------------------------------------------------------------------------------
    constructor(props) {
        super(props);
        this.state = {
            configuration: _.merge({}, DashboardHelper.getDefaultConfiguration(), this.props.configuration),
            errorMessagePageId: null
        };
    }
    // ------------------------------------------------------------------------------------------------------
    private onConfigurationClose = () => {
        let anyChanges = !isEqualsSafe(this.props.configuration, this.state.configuration);
        if (anyChanges && this.props.onConfigurationChange)
            this.props.onConfigurationChange(this.props.configurationKey, this.state.configuration);

        if (this.props.onConfigurationClose)
            this.props.onConfigurationClose(this.props.configurationKey);
    }
    // ------------------------------------------------------------------------------------------------------
    private onConfigurationCloseWithoutSave = () => {
        if (this.props.onConfigurationClose)
            this.props.onConfigurationClose(this.props.configurationKey);
    }
    // ------------------------------------------------------------------------------------------------------
    private renderBody() {
        const theme = BoardTheming.GetActiveTheme();
        const fontColor = theme.dialog.fontColor;

        const formElements: SettingsGroup<DashboardConfiguration>[] = [{
            name: '',
            elements: [{
                type: SettingsType.String,
                dataPath: 'Name',
                label: 'Name'
            },
            {
                type: SettingsType.String,
                dataPath: 'Desc',
                label: 'Description'
            },
            {
                type: SettingsType.String,
                dataPath: 'PageId',
                label: 'Page ID',
                validationErrorMsg: this.state.errorMessagePageId
            },
            {
                type: SettingsType.String,
                dataPath: 'Path',
                label: 'Path'
            },
            {
                type: SettingsType.Number,
                dataPath: 'Order',
                label: 'Order',
                minValue: 0
            },
            {
                type: SettingsType.Group,
                name: 'Layout',
                headerStyle: { backgroundColor: grey100 },
                elements: [{
                    type: SettingsType.Boolean,
                    dataPath: 'VerticalCompact',
                    label: 'Compact layout',
                    tooltip: 'Widgets are closely and neatly packed together',
                    oneLine: true
                },
                {
                    type: SettingsType.Boolean,
                    dataPath: 'GridLayout',
                    label: 'Grid layout',
                    tooltip: 'A framework of spaced bars that are parallel',
                    oneLine: true
                },
                {
                    type: SettingsType.Slider,
                    dataPath: 'NumberOfVerticalGridSpaces',
                    label: 'Vertical grid size',
                    tooltip: 'The number of columns displayed in the grid',
                    minValue: 6,
                    maxValue: 36,
                    stepValue: 6,
                    valuePosfix: ''
                },
                {
                    type: SettingsType.Slider,
                    dataPath: 'MaxPageWidth',
                    label: 'Max page width',
                    tooltip: 'The maximum width of canvas',
                    minValue: 500,
                    maxValue: 2600,
                    stepValue: 50,
                    valuePosfix: ''
                },
                {
                    type: SettingsType.Slider,
                    dataPath: 'Margin.W',
                    label: 'Horizontal margin',
                    tooltip: 'Space between the widgets. For no space set it to zero.',
                    minValue: 0,
                    maxValue: 20,
                    stepValue: 1,
                    valuePosfix: ''
                },
                {
                    type: SettingsType.Slider,
                    dataPath: 'Margin.H',
                    label: 'Vertical margin',
                    tooltip: 'Space between the widgets. For no space set it to zero.',
                    minValue: 0,
                    maxValue: 20,
                    stepValue: 1,
                    valuePosfix: ''
                },
                {
                    type: SettingsType.Slider,
                    dataPath: 'Padding',
                    label: 'Padding',
                    tooltip: 'The space around canvas',
                    minValue: 0,
                    maxValue: 20,
                    stepValue: 1,
                    valuePosfix: ''
                },
                {
                    type: SettingsType.Boolean,
                    dataPath: 'IsHidden',
                    label: 'Hidden',
                    tooltip: 'Hides the page from the list',
                    oneLine: true,
                    readonly: this.props.inEditMode
                },
                {
                    type: SettingsType.DropDown,
                    dataPath: 'LayoutPolicy',
                    label: 'Page layout policy',
                    inSeperateLine: true,
                    items: [
                        { label: 'Fill width', value: PageLayoutPolicy.FillWidth },
                        { label: 'Fill height', value: PageLayoutPolicy.FillHeight }
                    ]
                },
                {
                    type: SettingsType.Slider,
                    dataPath: 'WidthListThreshold',
                    label: 'Width Mobile Threshold',
                    minValue: 0,
                    maxValue: 1600,
                    stepValue: 10,
                    valuePosfix: '',
                    isNullable: true
                }]
            },
            {
                type: SettingsType.Group,
                name: 'Theme',
                headerStyle: { backgroundColor: grey100 },
                elements: [{
                    type: SettingsType.Icon,
                    dataPath: 'Icon',
                    label: 'Icon',
                    oneLine: true
                },
                {
                    type: SettingsType.ImageFileInput,
                    dataPath: 'BackgroundImage',
                    label: 'Desktop background image',
                    previewImg: true,
                    externalURL: false,
                    imgSettings: {
                        maxWidth: DashboardConfigurationView.MaxImageWidth,
                        maxHeight: DashboardConfigurationView.MaxImageHeight,
                        format: 'jpeg',
                        quality: 0.8
                    }
                },
                {
                    type: SettingsType.ImageFileInput,
                    dataPath: 'MobileBackgroundImage',
                    label: 'Mobile background image',
                    previewImg: true,
                    externalURL: false,
                    imgSettings: {
                        maxWidth: DashboardConfigurationView.MaxImageWidth,
                        maxHeight: DashboardConfigurationView.MaxImageHeight,
                        format: 'jpeg',
                        quality: 0.8
                    }
                },
                {
                    type: SettingsType.DropDown,
                    dataPath: 'OverrideCanvasBackgroundColor',
                    label: 'Override canvas background color',
                    inSeperateLine: false,
                    widthInput: 200,
                    items: BoardThemeColorOverrideItems
                },
                {
                    type: SettingsType.ColorPicker,
                    dataPath: 'CanvasBackgroundColor',
                    label: 'Canvas background color',
                    oneLine: true,
                    isHidden: data => data.OverrideCanvasBackgroundColor != BoardThemeColorOverride.StaticColor
                },
                {
                    type: SettingsType.DropDown,
                    dataPath: 'CanvasBackgroundColorKey',
                    label: 'Canvas background palette color',
                    inSeperateLine: false,
                    items: BoardTheming.GetActiveTheme()?.colorPalette,
                    widthInput: 200,
                    isHidden: data => data.OverrideCanvasBackgroundColor != BoardThemeColorOverride.PaletteColor
                },
                {
                    type: SettingsType.DropDown,
                    dataPath: 'OverrideWidgetBackgroundColor',
                    label: 'Override widgets background color',
                    inSeperateLine: false,
                    widthInput: 200,
                    items: BoardThemeColorOverrideItems
                },
                {
                    type: SettingsType.ColorPicker,
                    dataPath: 'WidgetBackgroundColor',
                    label: 'Widgets background color',
                    oneLine: true,
                    isHidden: data => data.OverrideWidgetBackgroundColor != BoardThemeColorOverride.StaticColor
                },
                {
                    type: SettingsType.DropDown,
                    dataPath: 'WidgetBackgroundColorKey',
                    label: 'Widgets background palette color',
                    inSeperateLine: false,
                    items: BoardTheming.GetActiveTheme()?.colorPalette,
                    widthInput: 200,
                    isHidden: data => data.OverrideWidgetBackgroundColor != BoardThemeColorOverride.PaletteColor
                },
                {
                    type: SettingsType.Boolean,
                    dataPath: 'WidgetNoBackground',
                    label: 'Transparent widgets',
                    oneLine: true
                },
                {
                    type: SettingsType.Boolean,
                    dataPath: 'OverrideZDepth',
                    label: 'Override zDepth',
                    oneLine: true
                },
                {
                    type: SettingsType.Slider,
                    dataPath: 'ZDepth',
                    label: 'zDepth',
                    minValue: 0,
                    maxValue: 20,
                    stepValue: 1,
                    valuePosfix: '',
                    isHidden: data => !data.OverrideZDepth
                },
                {
                    type: SettingsType.Boolean,
                    dataPath: 'OverrideBorderRadius',
                    label: 'Override border radius',
                    oneLine: true
                },
                {
                    type: SettingsType.Slider,
                    dataPath: 'BorderRadius',
                    label: 'Border radius',
                    minValue: 0,
                    maxValue: 20,
                    stepValue: 1,
                    valuePosfix: '',
                    isHidden: data => !data.OverrideBorderRadius
                },
                {
                    type: SettingsType.Slider,
                    dataPath: 'Outline.Thickness',
                    label: 'Outline thickness',
                    minValue: 0,
                    maxValue: 20,
                    stepValue: 1,
                    valuePosfix: 'px'
                },
                {
                    type: SettingsType.ColorPicker,
                    dataPath: 'Outline.Color',
                    label: 'Outline color',
                    oneLine: true
                }]
            },
            {
                type: SettingsType.Group,
                name: 'Cyan',
                headerStyle: { backgroundColor: grey100 },
                elements: [{
                    type: SettingsType.Group,
                    name: 'Polling',
                    headerStyle: { backgroundColor: grey100 },
                    elements: [{
                        type: SettingsType.Boolean,
                        dataPath: 'Polling.Enabled',
                        label: 'Enable polling',
                        oneLine: true
                    },
                    {
                        type: SettingsType.Boolean,
                        dataPath: 'Polling.EnabledOnlyInFocus',
                        label: 'Enabled only when in focus',
                        oneLine: true
                    },
                    {
                        type: SettingsType.Boolean,
                        dataPath: 'Polling.SendPollInRefocus',
                        label: 'Send after refocus',
                        tooltip: 'Sends poll request when browser\'s tab gets focus',
                        oneLine: true
                    },
                    {
                        type: SettingsType.DropDown,
                        dataPath: 'Polling.PeriodScale',
                        label: 'Scale',
                        inSeperateLine: true,
                        items: [
                            { label: 'Hours', value: PollingScale.Hours },
                            { label: 'Minutes', value: PollingScale.Min },
                            { label: 'Seconds', value: PollingScale.Sec }
                        ]
                    },
                    {
                        type: SettingsType.Slider,
                        dataPath: 'Polling.Period',
                        label: 'Period',
                        minValue: 1,
                        maxValue: 120,
                        stepValue: 1,
                        valuePosfix: ''
                    }]
                },
                {
                    type: SettingsType.Boolean,
                    dataPath: 'SessionBasedPortData',
                    label: 'Session based port events',
                    tooltip: 'Sends the data state for all widgets of the page with each event',
                    oneLine: true
                }]
            }]
        }];

        return <SettingsList
            AutoSave
            gridGap={2}
            SettingsMap={formElements}
            Data={this.state.configuration}
            style={{ color: fontColor }}
            onSaveData={data => {
                this.setState((s: DashboardConfigurationViewState) => {
                    s.configuration = { ...data };
                    if (!s.configuration.Path?.startsWith('/')) {
                        s.configuration.Path = `/${data.Path ?? ''}`;
                    }
                    while (s.configuration.Path?.indexOf('//') > -1) {
                        s.configuration.Path = s.configuration.Path.replace('//', '/');
                    }
                    return s;
                });
            }}
            onDataChange={data => {
                if (data.PageId != this.props.configuration.PageId)
                    this.setState((s: DashboardConfigurationViewState) => {
                        if (_.some(this.props.otherPages, x => x.PageId == data.PageId))
                            s.errorMessagePageId = 'Duplicate Page ID';
                        else
                            s.errorMessagePageId = null;
                        return s;
                    });
            }}
        />;
    }
    // ------------------------------------------------------------------------------------------------------
    render() {
        return (
            <ExDialog
                Enable={this.props.isOpen}
                Conf={{
                    title: `Page Settings (key: ${this.props.configurationKey})`,
                    actions: [
                        <IconButton
                            key="cancel"
                            tooltip="Cancel"
                            iconClassName="fas fa-times"
                            iconStyle={{ color: red200 }}
                            onClick={this.onConfigurationCloseWithoutSave}
                        />,
                        <IconButton
                            key="save"
                            tooltip="Save"
                            iconClassName="fas fa-cloud-upload-alt"
                            iconStyle={{ color: "white" }}
                            onClick={this.onConfigurationClose}
                        />
                    ],
                    contentStaticSize: { w: 0.45, h: 0.95 },
                    bodyFunc: () => this.renderBody()
                }}>
            </ExDialog>
        );
    }
    // ------------------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: DashboardConfigurationViewProps) {
        if (!_.isEqual(this.props.configuration, prevProps.configuration))
            this.setState({
                configuration: _.cloneDeep(this.props.configuration),
                errorMessagePageId: null
            });
    }
    // ------------------------------------------------------------------------------------------------------
}
// ------------------------------------------------------------------------------------------------------